import { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { OidcConfigurationNameContext } from "../../contexts/OidcConfigurationNameContext";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import { getUserInfo, BackendUserInfo } from "../../functions/requests/getUserInfo";
import { useAuthorizationError } from "../../contexts/AuthorizationErrorContext";
import { UserInfoContext } from "../../contexts/UserInfoContext";

export const UserInfoProvider = ({ children }: { children: ReactNode }) => {
  const { oidcConfigurationName } = useContext(OidcConfigurationNameContext);
  const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
  const { setAuthError } = useAuthorizationError();
  const [userInfo, setUserInfo] = useState<BackendUserInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const reloadUserInfo = useCallback(async () => {
    if (!oidcAccessToken?.accessToken) {
      console.log("UserInfoProvider: No access token available yet");
      return;
    }
    console.log("UserInfoProvider: Making API call to get user info");
    setLoading(true);
    setError(null);
    try {
      const userInfoResponse = await getUserInfo(oidcAccessToken);
      setUserInfo(userInfoResponse);
      console.log("UserInfoProvider: Successfully loaded user info with groups:", userInfoResponse.groups);
    } catch (err) {
      console.error("UserInfoProvider: API call failed:", err);
      
      // Enhanced error detection for 401 unauthorized errors
      let isAuthError = false;
      let errorMessage = "Unknown error";
      
      if (err instanceof Error) {
        errorMessage = err.message;
        console.log("UserInfoProvider: Error message:", errorMessage);
        
        // Check multiple ways a 401 error might be represented
        const lowerErrorMessage = errorMessage.toLowerCase();
        isAuthError = lowerErrorMessage.includes('401') || 
                     lowerErrorMessage.includes('unauthorized') ||
                     lowerErrorMessage.includes('access denied') ||
                     lowerErrorMessage.includes('forbidden');
      } else if (typeof err === 'object' && err !== null) {
        // Check if it's a fetch response error
        const errObj = err as any;
        if (errObj.status === 401 || errObj.statusCode === 401) {
          isAuthError = true;
          errorMessage = `HTTP ${errObj.status || errObj.statusCode}: Unauthorized`;
        }
        console.log("UserInfoProvider: Error object:", errObj);
      }
      
      if (isAuthError) {
        console.log("UserInfoProvider: 🚨 DETECTED AUTHORIZATION ERROR - Setting in context");
        console.log("UserInfoProvider: Error details:", {
          message: errorMessage,
          endpoint: '/user/me',
          provider: oidcConfigurationName,
          timestamp: new Date().toISOString()
        });
        
        // Set the authorization error in context
        setAuthError({
          message: errorMessage,
          endpoint: '/user/me',
          provider: oidcConfigurationName,
          timestamp: new Date().toISOString()
        });
        return; // Don't set error state, we're handling this via context
      } else {
        console.log("UserInfoProvider: Not an authorization error, setting normal error state");
        setError(err as Error);
      }
    } finally {
      setLoading(false);
    }
  }, [oidcAccessToken, oidcConfigurationName, setAuthError]);

  useEffect(() => {
    void reloadUserInfo();
  }, [reloadUserInfo]);

  return (
    <UserInfoContext.Provider
      value={{
        userInfo: userInfo,
        loading: loading,
        error: error,
        reload: reloadUserInfo,
        name: "user info",
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
}; 